import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HeartHandshake, Clock, MessageCircle, UserPlus, Heart, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, apiError, fileUrl } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../context/AuthContext";
import { useAppData } from "../../context/AppDataContext";

// Permission-first contact for Contactos profiles: request → accepted → chat.
export default function ContactRequest({ listing }) {
  const { t } = useLang();
  const { user } = useAuth();
  const { requireLogin } = useAppData();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [match, setMatch] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    api.get("/contact-requests/status", { params: { listing_id: listing.id } }).then((r) => setSt(r.data)).catch(() => {});
  }, [user, listing.id]);
  useEffect(() => {
    load();
  }, [load]);

  const request = async () => {
    if (!requireLogin() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/contact-requests", { listing_id: listing.id });
      setSt(data);
      if (data.matched) setMatch(true);
      else toast.success(t("contact.sentToast"));
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "profile_required" || d === "daily_limit" || d === "profile_incomplete") load();
      else toast.error(d === "blocked" ? t("msg.cantSend") : apiError(e, t));
    }
    setBusy(false);
  };
  const cancel = async () => {
    setBusy(true);
    try {
      await api.delete(`/contact-requests/${st.conversation_id}`);
      setSt({ ...st, state: "none", conversation_id: null });
    } catch (e) {
      toast.error(apiError(e, t));
    }
    setBusy(false);
  };

  if (user && st && !st.has_profile && st.state === "none")
    return (
      <div className="rounded-2xl bg-mist p-4 text-center" data-testid="contact-need-profile">
        <p className="text-sm font-semibold text-navy">{t("contact.needProfile")}</p>
        <Link to="/publicar?category=contactos" className="btn-primary mt-3 w-full py-3.5" data-testid="contact-create-profile-btn">
          <UserPlus className="h-5 w-5" /> {t("contact.createProfile")}
        </Link>
      </div>
    );
  if (user && st && st.has_profile && !st.profile_complete && st.state === "none")
    return (
      <div className="text-center" data-testid="contact-incomplete-profile">
        <Link to={`/editar/${st.profile_id}`} className="btn-primary w-full py-4 text-[15px]" data-testid="contact-complete-profile-btn">
          <Camera className="h-5 w-5" /> {t("contact.completeProfile")}
        </Link>
        <p className="mt-2 text-xs text-slate-500" data-testid="contact-photos-missing">
          {t("contact.needPhotos", { n: st.min_photos, x: st.min_photos - st.my_photos })}
        </p>
      </div>
    );
  if (st?.state === "accepted")
    return (
      <>
        <button className="btn-primary w-full py-4 text-lg" onClick={() => navigate(`/mensajes/${st.conversation_id}`)} data-testid="contact-open-chat-btn">
          <MessageCircle className="h-5 w-5" /> {t("contact.openChat")}
        </button>
        <MatchDialog open={match} onOpenChange={setMatch} me={st.my_photo} them={listing.photos[0]}
          onOpen={() => navigate(`/mensajes/${st.conversation_id}`)} />
      </>
    );
  if (st?.state === "pending")
    return (
      <div className="text-center">
        <button disabled className="btn-primary w-full cursor-default py-4 text-[15px] opacity-60" data-testid="contact-pending-btn">
          <Clock className="h-5 w-5" /> {t("contact.pending")}
        </button>
        <button onClick={cancel} disabled={busy} className="mt-2 text-sm font-semibold text-slate-500 underline underline-offset-2 hover:text-alert" data-testid="contact-cancel-btn">
          {t("contact.cancel")}
        </button>
      </div>
    );
  const limited = st && st.state === "none" && st.remaining === 0;
  return (
    <div className="text-center">
      <button className="btn-primary w-full py-4 text-lg" onClick={request}
        disabled={busy || (user && !st) || limited || st?.state === "closed"} data-testid="contact-request-btn">
        <HeartHandshake className="h-5 w-5" /> {t("contact.request")}
      </button>
      {limited ? (
        <p className="mt-2 text-xs font-semibold text-alert" data-testid="contact-limit-reached">{t("contact.limitReached", { n: st.daily_limit })}</p>
      ) : st && st.state !== "incoming" && st.remaining <= 5 ? (
        <p className="mt-2 text-xs text-slate-400" data-testid="contact-remaining">{t("contact.remaining", { n: st.remaining })}</p>
      ) : null}
    </div>
  );
}

function MatchDialog({ open, onOpenChange, me, them, onOpen }) {
  const { t } = useLang();
  const Photo = ({ src, tilt }) => (
    <div className={`h-24 w-24 overflow-hidden rounded-full bg-mist ring-4 ring-white shadow-lg ${tilt}`}>
      {src && <img src={fileUrl(src)} alt="" className="h-full w-full object-cover" />}
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-sm rounded-3xl border-0 p-7 text-center" data-testid="match-dialog">
        <div className="mx-auto flex items-center justify-center">
          <Photo src={me} tilt="-rotate-6" />
          <div className="z-10 -mx-3 flex h-11 w-11 items-center justify-center rounded-full bg-alert text-white shadow-lg">
            <Heart className="h-5 w-5 fill-white" />
          </div>
          <Photo src={them} tilt="rotate-6" />
        </div>
        <DialogTitle className="mt-4 font-serif text-3xl text-[#0D47A1]">{t("contact.matchTitle")}</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">{t("contact.matchSub")}</DialogDescription>
        <button className="btn-primary mt-2 w-full py-3.5" onClick={onOpen} data-testid="match-open-chat-btn">
          <MessageCircle className="h-5 w-5" /> {t("contact.openChat")}
        </button>
      </DialogContent>
    </Dialog>
  );
}
